import React, { useEffect, useState, Children, lazy, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ArrowRight, Heart, Coffee, BookOpen, DollarSign, Star, Lightbulb, FileText, CalendarCheck, Gift, ChevronDown, Lock, LockOpen, Sparkles, Map } from 'lucide-react';
// Declare fbq for TypeScript
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
// Initialize Meta Pixel
const initMetaPixel = () => {
  if (typeof window !== 'undefined' && !window.fbq) {
    const f = window as any;
    const n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    // Load the Facebook pixel script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    // Initialize with your pixel ID
    window.fbq('init', '2042259813067389');
  }
};
// Track page view
const trackPageView = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'PageView');
  }
};
// Track button click (InitiateCheckout event)
const trackInitiateCheckout = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_name: 'Couch Cash for Grandmas',
      content_category: 'Digital Product',
      value: 6.49,
      currency: 'USD'
    });
  }
};
// Modal Component
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  if (!isOpen) return null;
  return <>
      <AnimatePresence data-id="element-2">
        {isOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-4" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" data-id="element-5">
              <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-6">
                <h2 className="text-2xl font-bold text-[#1E3A5F]" data-id="element-7">{title}</h2>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close modal" data-id="element-8">
                  <X className="w-5 h-5 text-gray-500" data-id="element-9" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] text-gray-700 leading-relaxed" data-id="element-10">
                {children}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </>;
}
// Theme Colors
const colors = {
  bg: 'bg-[#FDF8F3]',
  bgAlt: 'bg-[#F5EDE4]',
  navy: 'text-[#1E3A5F]',
  gold: 'bg-[#D4A853]',
  goldText: 'text-[#D4A853]',
  sage: 'bg-[#E8F0EB]',
  text: 'text-gray-700',
  heading: 'text-[#1E3A5F]'
};
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
} as any;
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
} as any;
export function LandingPage() {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [bonusesExpanded, setBonusesExpanded] = useState(false);
  const [orderBumps, setOrderBumps] = useState({
    bump1: false,
    bump2: false,
    bump3: false
  });
  const [bump2JustUnlocked, setBump2JustUnlocked] = useState(false);
  // Check if both paid bumps are selected
  const bump2Unlocked = orderBumps.bump1 && orderBumps.bump3;
  // Handle auto-unlocking logic
  useEffect(() => {
    if (bump2Unlocked) {
      if (!orderBumps.bump2) {
        setOrderBumps(prev => ({
          ...prev,
          bump2: true
        }));
        setBump2JustUnlocked(true);
        const timer = setTimeout(() => setBump2JustUnlocked(false), 3000);
        return () => clearTimeout(timer);
      }
    } else {
      if (orderBumps.bump2) {
        setOrderBumps(prev => ({
          ...prev,
          bump2: false
        }));
      }
    }
  }, [bump2Unlocked, orderBumps.bump2]);
  // Compute dynamic total price
  const BASE_PRICE = 14.89;
  const BUMP_PRICE = 9.0;
  // Note: bump2 is FREE when unlocked, so we don't add its price
  const totalPrice = BASE_PRICE + (orderBumps.bump1 ? BUMP_PRICE : 0) + (orderBumps.bump3 ? BUMP_PRICE : 0);
  const totalDollars = Math.floor(totalPrice);
  const totalCents = Math.round((totalPrice - totalDollars) * 100).toString().padStart(2, '0');
  // Selected bump names for value breakdown
  const selectedBumpNames: string[] = [];
  if (orderBumps.bump3) selectedBumpNames.push('The "Kitchen Classics" Recipe Vault');
  if (orderBumps.bump1) selectedBumpNames.push('The "Buyer Scout" System');
  if (orderBumps.bump2) selectedBumpNames.push('The "No-Guess" Etsy Shop Map');
  // Initialize Meta Pixel and track page view on mount
  useEffect(() => {
    initMetaPixel();
    // Small delay to ensure pixel is loaded
    const timer = setTimeout(() => {
      trackPageView();
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  const scrollToPricing = () => {
    const element = document.getElementById('pricing');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Helper: compute the correct Shopify checkout permalink based on bump selection
  const getCheckoutUrl = () => {
    const hasVault = orderBumps.bump3;
    const hasScout = orderBumps.bump1;
    if (hasVault && hasScout) {
      // Main product + Recipe Vault + Buyer Scout + No-Guess Etsy Shop Map (all 3)
      return 'https://www.mellowreset.com/cart/43022180646978:1';
    } else if (hasVault) {
      // Main product + Recipe Vault only
      return 'https://www.mellowreset.com/cart/43013466325058:1';
    } else if (hasScout) {
      // Main product + Buyer Scout only
      return 'https://www.mellowreset.com/cart/43013466718274:1';
    } else {
      // Main product only
      return 'https://www.mellowreset.com/cart/42953644408898:1';
    }
  };
  return <div className={`min-h-screen ${colors.bg} overflow-x-hidden`} data-id="element-11">
      {/* Hero Section */}
      <section className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32" data-id="element-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-13">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-14">
            <motion.div variants={fadeInUp} className="space-y-4 text-lg md:text-xl text-gray-500 font-medium italic" data-id="element-15">
              <p className="line-through decoration-red-400 decoration-2" data-id="element-16">
                You don't need to stand for hours.
              </p>
              <p className="line-through decoration-red-400 decoration-2" data-id="element-17">
                You don't need to ship anything.
              </p>
              <p className="line-through decoration-red-400 decoration-2" data-id="element-18">
                You don't need to be on camera.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} data-id="element-19">
              <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold ${colors.heading} leading-tight mb-4`} data-id="element-20">
                Couch Cash <br data-id="element-21" />
                <span className="italic font-normal text-[#D4A853]" data-id="element-22">
                  for Grandmas
                </span>
              </h1>

              <p className={`text-xl md:text-2xl ${colors.text} leading-relaxed`} data-id="element-23">
                The 7-Day Guide to Make & Sell Printable Recipe Cards{' '}
                <span className="whitespace-nowrap" data-id="element-24">(From the Couch)</span>
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="p-6 bg-white/60 border-l-4 border-[#D4A853] rounded-r-lg backdrop-blur-sm" data-id="element-25">
              <p className="text-lg text-gray-600" data-id="element-26">
                <span className="font-bold text-[#1E3A5F]" data-id="element-27">Note:</span> This is{' '}
                <strong data-id="element-28">not</strong> a cookbook. You are <strong data-id="element-29">not</strong>{' '}
                buying pre-made recipe cards to cook with. You're learning to
                build a simple digital product.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} data-id="element-30">
              <button onClick={scrollToPricing} className={`${colors.gold} hover:bg-[#c29642] text-white text-xl font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 flex items-center gap-3`} data-id="element-31">
                Start Your 7-Day Journey <ArrowRight className="w-6 h-6" data-id="element-32" />
              </button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-33">
            <div className="absolute inset-0 bg-[#D4A853]/10 rounded-3xl transform rotate-3 scale-105 z-0" data-id="element-34"></div>

            <img src="https://cdn.magicpatterns.com/uploads/mB3HbJVidKYTWQBwNcz9WK/grandma_mockup.png" alt="Grandma holding a tablet showing the Couch Cash guide" className="relative z-10 rounded-2xl shadow-2xl w-full object-cover" data-id="element-35" />
          </motion.div>
        </div>
      </section>

      {/* Problem Section */}
      <section className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-36">
        <div className="max-w-3xl mx-auto text-center" data-id="element-37">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-38">
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-8`} data-id="element-39">
              Because here's the truth:
            </h2>
            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-12" data-id="element-40">
              A lot of grandmas already have something valuable. You've
              collected recipes for years. You know what people actually like.
              You know how to make something feel "home."
            </p>

            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm text-left relative overflow-hidden" data-id="element-41">
              <div className="absolute top-0 left-0 w-full h-2 bg-[#1E3A5F]" data-id="element-42"></div>
              <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6" data-id="element-43">
                But turning that into something you can sell online?
              </h3>
              <p className="text-lg text-gray-600 mb-6" data-id="element-44">
                That's where most people get stuck. Not because they're lazy.
                But because the internet makes it feel like you need:
              </p>
              <ul className="space-y-4" data-id="element-45">
                {['12 different apps to learn', 'Perfect branding and logos', "Hours of energy you don't have", 'A loud personality on social media'].map((item, i) => <li key={i} className="flex items-center gap-4 text-lg text-gray-500" data-id="element-46">
                    <X className="w-6 h-6 text-red-400 flex-shrink-0" data-id="element-47" />
                    <span className="line-through decoration-gray-300" data-id="element-48">
                      {item}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-8 text-lg font-medium text-[#1E3A5F] bg-[#E8F0EB] p-4 rounded-lg" data-id="element-49">
                If you're dealing with chronic pain or low energy, that kind of
                advice isn't just unhelpful—it's impossible.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solution Section: 1-1-1 Method */}
      <section className="py-20 px-4" data-id="element-50">
        <div className="max-w-4xl mx-auto" data-id="element-51">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-52">
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-6`} data-id="element-53">
              This guide is built for grandmas who need it simple.
            </h2>
            <p className="text-xl text-gray-600" data-id="element-54">
              Inside{' '}
              <span className="font-bold text-[#D4A853]" data-id="element-55">
                Couch Cash for Grandmas
              </span>
              , you follow one calm system:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 text-center" data-id="element-56">
            {[{
            icon: Heart,
            title: 'Pick 1 Theme',
            desc: 'Choose a recipe style you already know and love.'
          }, {
            icon: BookOpen,
            title: 'Make 1 Pack',
            desc: 'Create a simple printable set using our template.'
          }, {
            icon: DollarSign,
            title: 'Post in 1 Place',
            desc: "List it on Etsy. That's it. No complicated websites."
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="bg-white p-8 rounded-2xl shadow-md border-b-4 border-[#D4A853]" data-id="element-57">
                <div className="w-16 h-16 bg-[#FDF8F3] rounded-full flex items-center justify-center mx-auto mb-6 text-[#D4A853]" data-id="element-58">
                  <step.icon className="w-8 h-8" data-id="element-59" />
                </div>
                <h3 className="text-2xl font-bold text-[#1E3A5F] mb-3" data-id="element-60">
                  {step.title}
                </h3>
                <p className="text-gray-600" data-id="element-61">{step.desc}</p>
              </motion.div>)}
          </div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-center mt-12 text-lg text-gray-500 italic" data-id="element-62">
            No bouncing between platforms. No trying to learn everything before
            you start. <br data-id="element-63" />
            No "someday when I feel better."
          </motion.p>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-64">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center" data-id="element-65">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-66">
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-8`} data-id="element-67">
              What you'll have at the end of 7 days
            </h2>
            <ul className="space-y-6" data-id="element-68">
              {['A finished printable recipe card set (your first real digital product)', 'A properly saved PDF file ready for buyers to download', 'A complete Etsy listing (with fill-in-the-blank words)', 'Simple post scripts you can copy to share it (no camera required)'].map((item, i) => <li key={i} className="flex items-start gap-4" data-id="element-69">
                  <div className="bg-[#D4A853] rounded-full p-1 mt-1 flex-shrink-0" data-id="element-70">
                    <Check className="w-4 h-4 text-white" data-id="element-71" />
                  </div>
                  <span className="text-lg md:text-xl text-gray-700" data-id="element-72">
                    {item}
                  </span>
                </li>)}
            </ul>
            <p className="mt-8 text-lg font-medium text-[#1E3A5F]" data-id="element-73">
              It's not theory. It's "do this, then this, then this."
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 rounded-2xl shadow-lg rotate-2 border border-gray-100" data-id="element-74">
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center text-center h-full min-h-[300px]" data-id="element-75">
              <Star className="w-12 h-12 text-[#D4A853] mb-4" data-id="element-76" />
              <h3 className="text-2xl font-serif text-[#1E3A5F] mb-2" data-id="element-77">
                Grandma's Secret Cookie Recipes
              </h3>
              <p className="text-gray-500 mb-6" data-id="element-78">
                Printable Recipe Cards • Instant Download
              </p>
              <div className="w-full h-32 bg-[#FDF8F3] rounded-lg mb-4 flex items-center justify-center text-gray-400 text-sm" data-id="element-79">
                [Your Beautiful Product Here]
              </div>
              <span className="bg-[#E8F0EB] text-[#1E3A5F] px-4 py-1 rounded-full text-sm font-bold" data-id="element-80">
                Ready to Sell
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What's Inside Section */}
      <section className="py-20 px-4" data-id="element-81">
        <div className="max-w-4xl mx-auto" data-id="element-82">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100" data-id="element-83">
            <div className="bg-[#1E3A5F] p-8 md:p-10 text-center" data-id="element-84">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-85">
                What's inside the ebook
              </h2>
              <p className="text-[#D4A853] text-lg font-medium" data-id="element-86">
                A grandma-friendly 7-day plan
              </p>
            </div>

            <div className="p-8 md:p-12 grid md:grid-cols-2 gap-x-12 gap-y-6" data-id="element-87">
              {['How to choose a recipe card theme people search for', 'How to make cards using a simple drag-and-drop template', 'How to use LARGE PRINT for a premium, readable look', 'How to save your files the right way for clean printing', 'How to price your pack without undercharging', 'Step-by-step Etsy listing instructions', 'Copy/paste listing description (edit in minutes)', 'Tag ideas that make sense (no confusing SEO talk)', 'Simple social post templates (no face required)', "A 'bad pain day' plan for 5-minute progress"].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-88">
                  <div className="w-2 h-2 rounded-full bg-[#D4A853] mt-2.5 flex-shrink-0" data-id="element-89"></div>
                  <span className="text-lg text-gray-700" data-id="element-90">{item}</span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FREE Bonuses Section */}
      <section id="bonuses-section" className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-91">
        <div className="max-w-5xl mx-auto" data-id="element-92">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="text-center mb-12" data-id="element-93">
            <div className="inline-flex items-center gap-2 bg-[#D4A853]/20 text-[#D4A853] px-4 py-2 rounded-full text-sm font-bold mb-6" data-id="element-94">
              <Gift className="w-4 h-4" data-id="element-95" />
              SPECIAL BONUS BUNDLE
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-4`} data-id="element-96">
              3 Bonuses (Value $47) — FREE Today
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-97">
              If you want to move fast (and avoid the "I'll do it later" trap),
              I added something special.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white p-6 md:p-8 rounded-2xl shadow-sm mb-12 border-l-4 border-[#D4A853]" data-id="element-98">
            <p className="text-lg text-gray-700" data-id="element-99">
              When you get{' '}
              <span className="font-bold text-[#1E3A5F]" data-id="element-100">
                Couch Cash for Grandmas
              </span>
              , you're not just getting the 7-day ebook… You also get{' '}
              <span className="font-bold text-[#D4A853]" data-id="element-101">
                3 grandma-friendly bonuses
              </span>{' '}
              that make it easier to choose a theme, publish your listing, and
              actually finish—especially on low-energy days.
            </p>
          </motion.div>

          <div className="space-y-8" data-id="element-102">
            {/* Bonus 1 */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-103">
              <div className="grid md:grid-cols-[280px_1fr] gap-0" data-id="element-104">
                <div className="bg-[#F5EDE4] p-6 flex items-center justify-center" data-id="element-105">
                  <img src="https://cdn.magicpatterns.com/uploads/x7bGowKkw2NmDdH4f823Ut/bonus1.png" alt="The 5-Minute Theme Picker bonus" className="w-full max-w-[220px] rounded-lg shadow-lg" data-id="element-106" />
                </div>
                <div className="p-6 md:p-8" data-id="element-107">
                  <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-108">
                    <span className="bg-[#D4A853]/20 text-[#D4A853] px-3 py-1 rounded-full text-xs font-bold" data-id="element-109">
                      BONUS #1
                    </span>
                    <span className="text-gray-400 text-sm line-through" data-id="element-110">
                      Value: $47
                    </span>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold" data-id="element-111">
                      FREE TODAY
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F] mb-3 flex items-center gap-3" data-id="element-112">
                    <Lightbulb className="w-6 h-6 text-[#D4A853] flex-shrink-0" data-id="element-113" />
                    The 5-Minute Theme Picker (Grandma Edition)
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-114">
                    Too many ideas? This bonus helps you choose ONE recipe card
                    theme in minutes—so you don't overthink and quit before you
                    start.
                  </p>
                  <p className="text-gray-600 mb-4" data-id="element-115">
                    You'll get a simple worksheet + decision tool that makes Day
                    1 easy.
                  </p>
                  <p className="font-medium text-[#1E3A5F] mb-2" data-id="element-116">
                    You'll use it to:
                  </p>
                  <ul className="space-y-2" data-id="element-117">
                    {['Pick a theme fast', 'Stop second-guessing', 'Commit to one pack you can finish'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-700" data-id="element-118">
                        <Check className="w-4 h-4 text-[#8DA399] flex-shrink-0" data-id="element-119" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.div>

            {/* Bonus 2 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-120">
              <div className="grid md:grid-cols-[280px_1fr] gap-0" data-id="element-121">
                <div className="bg-[#F5EDE4] p-6 flex items-center justify-center md:order-last" data-id="element-122">
                  <img src="https://cdn.magicpatterns.com/uploads/c9gcbgmvgMAj6eJM3apzn4/bonus2.png" alt="Copy-Paste Etsy Listing Kit bonus" className="w-full max-w-[220px] rounded-lg shadow-lg" data-id="element-123" />
                </div>
                <div className="p-6 md:p-8" data-id="element-124">
                  <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-125">
                    <span className="bg-[#D4A853]/20 text-[#D4A853] px-3 py-1 rounded-full text-xs font-bold" data-id="element-126">
                      BONUS #2
                    </span>
                    <span className="text-gray-400 text-sm line-through" data-id="element-127">
                      Value: $47
                    </span>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold" data-id="element-128">
                      FREE TODAY
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F] mb-3 flex items-center gap-3" data-id="element-129">
                    <FileText className="w-6 h-6 text-[#D4A853] flex-shrink-0" data-id="element-130" />
                    Copy-Paste Etsy Listing Kit (Recipe Cards)
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-131">
                    This is the "what do I write?" problem solved.
                  </p>
                  <p className="font-medium text-[#1E3A5F] mb-2" data-id="element-132">
                    You get fill-in-the-blank words for:
                  </p>
                  <ul className="space-y-2 mb-4" data-id="element-133">
                    {['Your Etsy title', 'Your description', 'FAQs (instant download / printing)', 'Tags + photo checklist'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-700" data-id="element-134">
                        <Check className="w-4 h-4 text-[#8DA399] flex-shrink-0" data-id="element-135" />
                        {item}
                      </li>)}
                  </ul>
                  <p className="text-gray-600 italic" data-id="element-136">
                    So you can publish without staring at a blank screen.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Bonus 3 */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white rounded-2xl shadow-md overflow-hidden" data-id="element-137">
              <div className="grid md:grid-cols-[280px_1fr] gap-0" data-id="element-138">
                <div className="bg-[#F5EDE4] p-6 flex items-center justify-center" data-id="element-139">
                  <img src="https://cdn.magicpatterns.com/uploads/aUXLJ65BucFNMzwwYpnVJd/bonus3.png" alt="The 7-Day Couch Tracker bonus" className="w-full max-w-[220px] rounded-lg shadow-lg" data-id="element-140" />
                </div>
                <div className="p-6 md:p-8" data-id="element-141">
                  <div className="flex flex-wrap items-center gap-3 mb-3" data-id="element-142">
                    <span className="bg-[#D4A853]/20 text-[#D4A853] px-3 py-1 rounded-full text-xs font-bold" data-id="element-143">
                      BONUS #3
                    </span>
                    <span className="text-gray-400 text-sm line-through" data-id="element-144">
                      Value: $47
                    </span>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold" data-id="element-145">
                      FREE TODAY
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1E3A5F] mb-3 flex items-center gap-3" data-id="element-146">
                    <CalendarCheck className="w-6 h-6 text-[#D4A853] flex-shrink-0" data-id="element-147" />
                    The 7-Day Couch Tracker (Printable + Mobile)
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-148">
                    This is what keeps you moving when energy is low.
                  </p>
                  <p className="text-gray-600" data-id="element-149">
                    A big-font tracker that tells you exactly what to do each
                    day—plus a "bad pain day" menu of 5-minute tasks so you
                    don't fall off.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Why Free + CTA */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="mt-12 text-center" data-id="element-150">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 max-w-2xl mx-auto" data-id="element-151">
              <h3 className="text-xl font-bold text-[#1E3A5F] mb-4" data-id="element-152">
                Why I'm including these free
              </h3>
              <p className="text-gray-600 mb-4" data-id="element-153">
                Because the goal isn't just reading an ebook.
              </p>
              <p className="text-lg font-medium text-[#1E3A5F] mb-6" data-id="element-154">
                The goal is getting to that moment where your first printable
                pack is finished and listed.
              </p>
              <div className="border-t border-gray-100 pt-6" data-id="element-155">
                <p className="text-gray-600 mb-4" data-id="element-156">
                  So if you're ready to keep this simple…
                </p>
                <p className="text-lg font-bold text-[#1E3A5F]" data-id="element-157">
                  Get Couch Cash for Grandmas now and you'll receive all 3
                  bonuses FREE{' '}
                  <span className="text-[#D4A853]" data-id="element-158">(total value $47)</span>{' '}
                  instantly.
                </p>
              </div>
            </div>

            <button onClick={scrollToPricing} className="mt-8 bg-[#D4A853] hover:bg-[#c29642] text-white text-xl font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 inline-flex items-center gap-3" data-id="element-159">
              Get the Guide + All 3 Bonuses <ArrowRight className="w-6 h-6" data-id="element-160" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Who it's for / Not for */}
      <section className={`py-20 px-4 ${colors.bgAlt}`} data-id="element-161">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-162">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-8 border-[#8DA399]" data-id="element-163">
            <h3 className="text-2xl font-bold text-[#1E3A5F] mb-6 flex items-center gap-3" data-id="element-164">
              <Check className="w-6 h-6 text-[#8DA399]" data-id="element-165" />
              This is for you if:
            </h3>
            <ul className="space-y-4" data-id="element-166">
              {["You're a grandma (50+) and want extra income", "You're on the couch/bed a lot (pain, low energy, recovery)", 'You want something simple you can finish', 'You want to sell a digital product (no shipping, no inventory)'].map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-gray-700" data-id="element-167">
                  <span className="text-[#8DA399] font-bold" data-id="element-168">•</span>
                  {item}
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-8 border-gray-300" data-id="element-169">
            <h3 className="text-2xl font-bold text-gray-600 mb-6 flex items-center gap-3" data-id="element-170">
              <X className="w-6 h-6 text-gray-400" data-id="element-171" />
              This is NOT for you if:
            </h3>
            <ul className="space-y-4" data-id="element-172">
              {['You want money without doing anything', 'You refuse to do any digital steps at all', 'You only want to sell physical products that require shipping'].map((item, i) => <li key={i} className="flex items-start gap-3 text-lg text-gray-500" data-id="element-173">
                  <span className="text-gray-400 font-bold" data-id="element-174">•</span>
                  {item}
                </li>)}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Why Recipe Cards */}
      <section className="py-20 px-4" data-id="element-175">
        <div className="max-w-3xl mx-auto text-center" data-id="element-176">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-177">
            <div className="inline-block p-3 bg-[#FDF8F3] rounded-full mb-6" data-id="element-178">
              <Coffee className="w-8 h-8 text-[#D4A853]" data-id="element-179" />
            </div>
            <h2 className={`text-3xl md:text-4xl font-bold ${colors.heading} mb-8`} data-id="element-180">
              Why recipe cards?
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-181">
              Because recipe cards are one of the easiest "first products" for
              grandmas:
            </p>

            <div className="grid sm:grid-cols-2 gap-6 text-left mb-12" data-id="element-182">
              {[{
              title: 'Familiar',
              desc: "Doesn't feel techy or scary"
            }, {
              title: 'Quick',
              desc: 'Make in small sessions'
            }, {
              title: 'Scalable',
              desc: 'Easy to bundle later for more income'
            }, {
              title: 'In Demand',
              desc: 'Buyers love printable kitchen tools'
            }].map((item, i) => <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm" data-id="element-183">
                  <h4 className="font-bold text-[#1E3A5F] text-lg mb-1" data-id="element-184">
                    {item.title}
                  </h4>
                  <p className="text-gray-600" data-id="element-185">{item.desc}</p>
                </div>)}
            </div>

            <p className="text-lg font-medium text-[#1E3A5F] italic" data-id="element-186">
              You're not trying to become an influencer. <br data-id="element-187" />
              You're creating something clear, useful, and sellable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="pricing" className={`py-24 px-4 bg-[#1E3A5F] text-white text-center`} data-id="element-188">
        <div className="max-w-4xl mx-auto" data-id="element-189">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} data-id="element-190">
            <h2 className="text-3xl md:text-5xl font-bold font-serif mb-6" data-id="element-191">
              Ready to make something you can sell from the couch?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-192">
              If you want a simple plan you can follow—without being
              overwhelmed—this guide was made for you.
            </p>

            <div className="bg-[#0f2340] p-8 rounded-3xl border-2 border-[#D4A853] max-w-lg mx-auto mb-10 shadow-[0_0_20px_rgba(212,168,83,0.25)]" data-id="element-193">
              <h3 className="text-2xl font-bold text-[#D4A853] mb-2" data-id="element-194">
                Couch Cash for Grandmas
              </h3>
              <p className="text-gray-300 mb-1" data-id="element-195">The Complete 7-Day Guide</p>
              <p className="text-gray-400 text-sm mb-4" data-id="element-196">(EBOOK)</p>

              {/* Dynamic Price Display */}
              <div className="mb-6" data-id="element-197">
                <div className="flex items-center justify-center gap-3" data-id="element-198">
                  <span className="text-5xl md:text-6xl font-bold text-white" style={{
                  fontFamily: 'Inter, system-ui, sans-serif'
                }} data-id="element-199">
                    ${totalDollars}
                    <span className="text-2xl md:text-3xl align-super" data-id="element-200">
                      .{totalCents}
                    </span>
                  </span>
                  <span className="text-lg text-gray-400 line-through" data-id="element-201">
                    $97
                  </span>
                </div>
                <span className="inline-block mt-2 bg-[#D4A853]/20 text-[#D4A853] px-3 py-1 rounded-full text-sm font-semibold" data-id="element-202">
                  85% OFF
                </span>
              </div>

              {/* What's Included */}
              <div className="bg-white/5 rounded-xl p-4 mb-6 border border-white/10 text-left" data-id="element-203">
                <p className="text-xs font-bold text-[#D4A853] uppercase tracking-wide mb-3 text-center" data-id="element-204">
                  This Ebook Includes
                </p>
                <ul className="space-y-2" data-id="element-205">
                  {['7-day step-by-step ebook to help you take action fast', "Simple, fill-in templates so you're never starting from scratch", 'Easy Etsy posting guide that walks you through listing it', 'Quick-start checklists to keep you focused each day'].map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-300" data-id="element-206">
                      <Check className="w-4 h-4 text-[#8DA399] flex-shrink-0 mt-0.5" data-id="element-207" />
                      <span data-id="element-208">{item}</span>
                    </li>)}
                </ul>
              </div>

              {/* Bonuses in Pricing */}
              <div className="bg-[#D4A853]/40 rounded-xl p-4 mb-6 border border-[#D4A853]/60" data-id="element-209">
                <div className="flex items-center justify-center gap-2 mb-3" data-id="element-210">
                  <Gift className="w-4 h-4 text-[#D4A853]" data-id="element-211" />
                  <span className="text-sm font-bold text-[#D4A853]" data-id="element-212">
                    + 3 FREE BONUSES (Value $47)
                  </span>
                </div>
                <div className="space-y-2" data-id="element-213">
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2" data-id="element-214">
                    <img src="https://cdn.magicpatterns.com/uploads/x7bGowKkw2NmDdH4f823Ut/bonus1.png" alt="Theme Picker Bonus" className="w-12 h-14 object-cover rounded-md" data-id="element-215" />
                    <p className="text-xs text-white font-medium" data-id="element-216">
                      The 5-Minute Theme Picker
                    </p>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2" data-id="element-217">
                    <img src="https://cdn.magicpatterns.com/uploads/c9gcbgmvgMAj6eJM3apzn4/bonus2.png" alt="Etsy Listing Kit Bonus" className="w-12 h-14 object-cover rounded-md" data-id="element-218" />
                    <p className="text-xs text-white font-medium" data-id="element-219">
                      Copy-Paste Etsy Listing Kit
                    </p>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-lg p-2" data-id="element-220">
                    <img src="https://cdn.magicpatterns.com/uploads/aUXLJ65BucFNMzwwYpnVJd/bonus3.png" alt="7-Day Tracker Bonus" className="w-12 h-14 object-cover rounded-md" data-id="element-221" />
                    <p className="text-xs text-white font-medium" data-id="element-222">
                      The 7-Day Couch Tracker
                    </p>
                  </div>
                </div>

                {/* Expandable Read More */}
                <button onClick={() => setBonusesExpanded(!bonusesExpanded)} className="mt-3 text-xs text-[#D4A853] hover:text-white transition-colors w-full text-center flex items-center justify-center gap-1" data-id="element-223">
                  {bonusesExpanded ? 'Hide details' : 'Read more about bonuses'}
                  <ChevronDown className={`w-3 h-3 transition-transform ${bonusesExpanded ? 'rotate-180' : ''}`} data-id="element-224" />
                </button>

                <AnimatePresence data-id="element-225">
                  {bonusesExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} className="overflow-hidden" data-id="element-226">
                      <div className="mt-4 space-y-4 text-left" data-id="element-227">
                        {/* Bonus 1 Detail */}
                        <div className="bg-white/10 rounded-lg p-4" data-id="element-228">
                          <div className="flex flex-col gap-3" data-id="element-229">
                            <img src="https://cdn.magicpatterns.com/uploads/x7bGowKkw2NmDdH4f823Ut/bonus1.png" alt="The 5-Minute Theme Picker bonus" className="w-full h-40 object-cover rounded-lg shadow-lg" data-id="element-230" />
                            <div className="flex-1" data-id="element-231">
                              <div className="flex items-center gap-2 mb-1" data-id="element-232">
                                <span className="bg-[#D4A853]/30 text-[#D4A853] px-2 py-0.5 rounded-full text-[10px] font-bold" data-id="element-233">
                                  BONUS #1
                                </span>
                                <span className="text-gray-400 text-[10px] line-through" data-id="element-234">
                                  Value: $47
                                </span>
                                <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full text-[10px] font-bold" data-id="element-235">
                                  FREE
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mb-1" data-id="element-236">
                                <Lightbulb className="w-3.5 h-3.5 text-[#D4A853]" data-id="element-237" />
                                <span className="text-xs font-bold text-white" data-id="element-238">
                                  The 5-Minute Theme Picker (Grandma Edition)
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-300 mb-2" data-id="element-239">
                                Too many ideas? This bonus helps you choose ONE
                                recipe card theme in minutes—so you don't
                                overthink and quit before you start.
                              </p>
                              <p className="text-[11px] text-gray-300 mb-1" data-id="element-240">
                                You'll get a simple worksheet + decision tool
                                that makes Day 1 easy.
                              </p>
                              <p className="text-[10px] font-medium text-white mb-1" data-id="element-241">
                                You'll use it to:
                              </p>
                              <ul className="space-y-0.5" data-id="element-242">
                                {['Pick a theme fast', 'Stop second-guessing', 'Commit to one pack you can finish'].map((item, i) => <li key={i} className="flex items-center gap-1.5 text-[10px] text-gray-300" data-id="element-243">
                                    <Check className="w-2.5 h-2.5 text-[#8DA399]" data-id="element-244" />
                                    {item}
                                  </li>)}
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Bonus 2 Detail */}
                        <div className="bg-white/10 rounded-lg p-4" data-id="element-245">
                          <div className="flex flex-col gap-3" data-id="element-246">
                            <img src="https://cdn.magicpatterns.com/uploads/c9gcbgmvgMAj6eJM3apzn4/bonus2.png" alt="Copy-Paste Etsy Listing Kit bonus" className="w-full h-40 object-cover rounded-lg shadow-lg" data-id="element-247" />
                            <div className="flex-1" data-id="element-248">
                              <div className="flex items-center gap-2 mb-1" data-id="element-249">
                                <span className="bg-[#D4A853]/30 text-[#D4A853] px-2 py-0.5 rounded-full text-[10px] font-bold" data-id="element-250">
                                  BONUS #2
                                </span>
                                <span className="text-gray-400 text-[10px] line-through" data-id="element-251">
                                  Value: $47
                                </span>
                                <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full text-[10px] font-bold" data-id="element-252">
                                  FREE
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mb-1" data-id="element-253">
                                <FileText className="w-3.5 h-3.5 text-[#D4A853]" data-id="element-254" />
                                <span className="text-xs font-bold text-white" data-id="element-255">
                                  Copy-Paste Etsy Listing Kit (Recipe Cards)
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-300 mb-2" data-id="element-256">
                                This is the "what do I write?" problem solved.
                              </p>
                              <p className="text-[10px] font-medium text-white mb-1" data-id="element-257">
                                You get fill-in-the-blank words for:
                              </p>
                              <ul className="space-y-0.5 mb-1" data-id="element-258">
                                {['Your Etsy title', 'Your description', 'FAQs (instant download / printing)', 'Tags + photo checklist'].map((item, i) => <li key={i} className="flex items-center gap-1.5 text-[10px] text-gray-300" data-id="element-259">
                                    <Check className="w-2.5 h-2.5 text-[#8DA399]" data-id="element-260" />
                                    {item}
                                  </li>)}
                              </ul>
                              <p className="text-[10px] text-gray-400 italic" data-id="element-261">
                                So you can publish without staring at a blank
                                screen.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Bonus 3 Detail */}
                        <div className="bg-white/10 rounded-lg p-4" data-id="element-262">
                          <div className="flex flex-col gap-3" data-id="element-263">
                            <img src="https://cdn.magicpatterns.com/uploads/aUXLJ65BucFNMzwwYpnVJd/bonus3.png" alt="The 7-Day Couch Tracker bonus" className="w-full h-40 object-cover rounded-lg shadow-lg" data-id="element-264" />
                            <div className="flex-1" data-id="element-265">
                              <div className="flex items-center gap-2 mb-1" data-id="element-266">
                                <span className="bg-[#D4A853]/30 text-[#D4A853] px-2 py-0.5 rounded-full text-[10px] font-bold" data-id="element-267">
                                  BONUS #3
                                </span>
                                <span className="text-gray-400 text-[10px] line-through" data-id="element-268">
                                  Value: $47
                                </span>
                                <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full text-[10px] font-bold" data-id="element-269">
                                  FREE
                                </span>
                              </div>
                              <div className="flex items-center gap-1.5 mb-1" data-id="element-270">
                                <CalendarCheck className="w-3.5 h-3.5 text-[#D4A853]" data-id="element-271" />
                                <span className="text-xs font-bold text-white" data-id="element-272">
                                  The 7-Day Couch Tracker (Printable + Mobile)
                                </span>
                              </div>
                              <p className="text-[11px] text-gray-300 mb-1" data-id="element-273">
                                This is what keeps you moving when energy is
                                low.
                              </p>
                              <p className="text-[11px] text-gray-300" data-id="element-274">
                                A big-font tracker that tells you exactly what
                                to do each day—plus a "bad pain day" menu of
                                5-minute tasks so you don't fall off.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Start Now CTA - between bonuses and order bumps */}
              <a href={getCheckoutUrl()} onClick={trackInitiateCheckout} className="block w-full bg-[#D4A853] hover:bg-[#c29642] text-white text-xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 mb-2 text-center" data-id="element-275">
                Start Now
              </a>

              {/* Motivational Text for Unlock */}
              <div className="text-center mb-4" data-id="element-276">
                <span className="font-semibold gold-pulse text-[14px] text-[#FFFFFF]" data-id="element-277">
                  ⬇️ Select 2 add-ons and unlock the EXCLUSIVE FREE GUIDE ⬇️
                </span>
              </div>

              {/* Order Bumps */}
              <div className="space-y-3 mb-4" data-id="element-278">
                {/* Order Bump - Recipe Vault (FIRST) */}
                <div onClick={() => setOrderBumps(prev => ({
                ...prev,
                bump3: !prev.bump3
              }))} className={`cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 ${orderBumps.bump3 ? 'border-[#D4A853] bg-[#D4A853]/20 shadow-[0_0_16px_rgba(212,168,83,0.35)]' : 'border-[#D4A853] bg-[#D4A853]/5 order-bump-flash'}`} data-id="element-279">
                  {/* Gold Header Bar */}
                  <div className="bg-[#D4A853] px-3 py-2 flex items-center gap-2" data-id="element-280">
                    <span className="font-bold tracking-widest uppercase text-[14px] text-[#000000]" data-id="element-281">
                      ⭐ Highly Recommended
                    </span>
                  </div>
                  {/* Content */}
                  <div className="p-3 flex gap-3" data-id="element-282">
                    <div className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center transition-all ${orderBumps.bump3 ? 'bg-[#D4A853] border-[#D4A853]' : 'border-gray-400 bg-transparent'}`} data-id="element-283">
                      {orderBumps.bump3 && <Check className="w-3 h-3 text-white" data-id="element-284" />}
                    </div>
                    <img src="https://cdn.magicpatterns.com/uploads/i91tL2Y6PHgzQqCVfyhGDP/kitchen_classics_recipe_vault.png" alt="Kitchen Classics Recipe Vault" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 shadow-md" data-id="element-285" />
                    <div className="flex-1 min-w-0" data-id="element-286">
                      <p className="text-sm font-bold text-white leading-snug mb-0.5" data-id="element-287">
                        The "Kitchen Classics" Recipe Vault
                      </p>
                      <p className="text-xs font-semibold text-[#D4A853] mb-1.5" data-id="element-288">
                        + $9.00
                      </p>
                      <p className="text-[11px] leading-relaxed text-[#FFFFFF] pl-[0px] pr-[0px]" data-id="element-289">
                        Copy-paste vault of 20 whole-food recipes (10
                        Diabetic-Friendly + 10 Family Favorites). Already done
                        for you—go from "Idea" to "Finished Product" today.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Bump - Buyer Scout (SECOND) */}
                <div onClick={() => setOrderBumps(prev => ({
                ...prev,
                bump1: !prev.bump1
              }))} className={`cursor-pointer rounded-xl border-2 overflow-hidden transition-all duration-300 ${orderBumps.bump1 ? 'border-[#D4A853] bg-[#D4A853]/20 shadow-[0_0_16px_rgba(212,168,83,0.35)]' : 'border-[#D4A853] bg-[#D4A853]/5 order-bump-flash'}`} data-id="element-290">
                  {/* Gold Header Bar */}
                  <div className="bg-[#D4A853] px-3 py-2 flex items-center gap-2" data-id="element-291">
                    <span className="font-bold tracking-widest uppercase text-[14px] text-[#000000]" data-id="element-292">
                      ⭐ Highly Recommended
                    </span>
                  </div>
                  {/* Content */}
                  <div className="p-3 flex gap-3" data-id="element-293">
                    <div className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center transition-all ${orderBumps.bump1 ? 'bg-[#D4A853] border-[#D4A853]' : 'border-gray-400 bg-transparent'}`} data-id="element-294">
                      {orderBumps.bump1 && <Check className="w-3 h-3 text-white" data-id="element-295" />}
                    </div>
                    <img src="https://cdn.magicpatterns.com/uploads/7zRdUbGKW9S9F4akUdEUyj/buyer_scout_system.png" alt="Buyer Scout System" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 shadow-md" data-id="element-296" />
                    <div className="flex-1 min-w-0" data-id="element-297">
                      <p className="text-sm font-bold text-white leading-snug mb-0.5" data-id="element-298">
                        The "Buyer Scout" System
                      </p>
                      <p className="text-xs font-semibold text-[#D4A853] mb-1.5" data-id="element-299">
                        + $9.00
                      </p>
                      <p className="text-[11px] leading-relaxed text-[#FFFFFF]" data-id="element-300">
                        Find "hidden" Facebook groups where 500,000+ recipe
                        lovers hang out. Includes the Magic 2-Sentence Script to
                        get customers asking for your Etsy link. No ads, no
                        tech-headaches.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Bump - No-Guess Etsy (THIRD - LOCKED/UNLOCKED) */}
                <AnimatePresence mode="wait" data-id="element-301">
                  {bump2Unlocked ? <motion.div key="unlocked" initial={{
                  opacity: 0,
                  scale: 0.95
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.95
                }} className={`relative rounded-xl border-2 overflow-hidden transition-all duration-300 border-[#D4A853] bg-[#D4A853]/20 shadow-[0_0_20px_rgba(212,168,83,0.5)] ${bump2JustUnlocked ? 'unlock-shimmer' : ''}`} data-id="element-302">
                      {/* Celebration Banner for Just Unlocked */}
                      <AnimatePresence data-id="element-303">
                        {bump2JustUnlocked && <motion.div initial={{
                      y: -20,
                      opacity: 0
                    }} animate={{
                      y: 0,
                      opacity: 1
                    }} exit={{
                      y: -20,
                      opacity: 0
                    }} className="absolute top-0 left-0 right-0 z-20 bg-[#D4A853] text-black font-bold text-center py-1 text-xs shadow-md flex items-center justify-center gap-2 celebration-pop" data-id="element-304">
                            <Sparkles className="w-3 h-3" data-id="element-305" />
                            🎉 YOU UNLOCKED THE EXCLUSIVE GUIDE!
                          </motion.div>}
                      </AnimatePresence>

                      {/* Header Bar */}
                      <div className="bg-gradient-to-r from-[#D4A853] to-[#E8C36A] px-3 py-2 flex items-center gap-2 mt-0" data-id="element-306">
                        <span className="font-bold tracking-widest uppercase text-[12px] md:text-[14px] text-black flex items-center gap-2" data-id="element-307"></span>
                      </div>

                      {/* Content */}
                      <div className="p-3 flex gap-3" data-id="element-308">
                        <div className="w-5 h-5 rounded flex-shrink-0 mt-0.5 border-2 flex items-center justify-center transition-all bg-[#D4A853] border-[#D4A853]" data-id="element-309">
                          <Check className="w-3 h-3 text-white" data-id="element-310" />
                        </div>
                        <img src="https://cdn.magicpatterns.com/uploads/wDYFknxxgvPLZnPaxisfwi/no_guess_etsy_shop.png" alt="No-Guess Etsy Shop Map" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 shadow-md" data-id="element-311" />
                        <div className="flex-1 min-w-0" data-id="element-312">
                          <p className="text-sm font-bold text-white leading-snug mb-0.5" data-id="element-313">
                            The "No-Guess" Etsy Shop Map
                          </p>
                          <p className="text-xs font-semibold text-green-400 mb-1.5 flex items-center gap-1" data-id="element-314">
                            <span className="line-through text-gray-400" data-id="element-315">
                              $9
                            </span>{' '}
                            FREE
                          </p>
                          <p className="text-[11px] leading-relaxed text-[#FFFFFF]" data-id="element-316">
                            Setup your Etsy shop with zero "tech-stress." Follow
                            this visual map to see exactly which buttons to
                            click for a 100% correct setup of your shop, taxes,
                            and bank info the first time.
                          </p>
                        </div>
                      </div>
                    </motion.div> : <motion.div key="locked" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="rounded-xl border-2 border-dashed border-gray-500/40 bg-white/5 overflow-hidden transition-all duration-300 opacity-60" data-id="element-317">
                      {/* Locked Header Bar */}
                      <div className="bg-gray-600 px-3 py-2 flex items-center gap-2" data-id="element-318">
                        <span className="font-bold tracking-widest uppercase md:text-[14px] text-gray-300 flex items-center gap-2 text-[14px]" data-id="element-319">
                          <Lock className="w-4 h-4" data-id="element-320" />
                          LOCKED — Select both add-ons to unlock FREE
                        </span>
                      </div>

                      {/* Content */}
                      <div className="p-3 flex gap-3" data-id="element-321">
                        <div className="w-5 h-5 rounded flex-shrink-0 mt-0.5 flex items-center justify-center" data-id="element-322">
                          <Lock className="w-4 h-4 text-gray-500" data-id="element-323" />
                        </div>
                        <img src="https://cdn.magicpatterns.com/uploads/wDYFknxxgvPLZnPaxisfwi/no_guess_etsy_shop.png" alt="No-Guess Etsy Shop Map Locked" className="w-14 h-14 object-cover rounded-lg flex-shrink-0 shadow-md grayscale opacity-70" data-id="element-324" />
                        <div className="flex-1 min-w-0" data-id="element-325">
                          <p className="text-sm font-bold text-gray-400 leading-snug mb-0.5" data-id="element-326">
                            The "No-Guess" Etsy Shop Map
                          </p>
                          <p className="text-xs font-semibold text-gray-500 mb-1.5 flex items-center gap-1" data-id="element-327">
                            <span className="line-through" data-id="element-328">$9</span>{' '}
                            <span className="text-green-700/50" data-id="element-329">FREE</span>
                          </p>
                          <p className="text-[11px] leading-relaxed text-gray-400" data-id="element-330">
                            Setup your Etsy shop with zero "tech-stress." Follow
                            this visual map to see exactly which buttons to
                            click for a 100% correct setup of your shop, taxes,
                            and bank info the first time.
                          </p>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>

              {/* Value Breakdown */}
              <div className="pt-2 text-left" data-id="element-331">
                <div className="space-y-2 mb-3" data-id="element-332">
                  <div className="flex items-center justify-between text-sm" data-id="element-333">
                    <span className="text-gray-300" data-id="element-334">
                      Couch Cash for Grandmas{' '}
                      <span className="text-xs text-gray-500" data-id="element-335">(ebook)</span>
                    </span>
                    <span className="text-gray-400 line-through" data-id="element-336">$97.00</span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-337">
                    <span className="text-gray-300" data-id="element-338">3 Free Bonuses</span>
                    <span className="text-gray-400 line-through" data-id="element-339">$47.00</span>
                  </div>
                  {/* Selected order bumps */}
                  {selectedBumpNames.map((name, i) => <div key={i} className="flex items-center justify-between text-sm" data-id="element-340">
                      <span className="text-[#D4A853] font-medium" data-id="element-341">{name}</span>
                      <span className={`font-medium ${name.includes('No-Guess') ? 'text-green-400' : 'text-[#D4A853]'}`} data-id="element-342">
                        {name.includes('No-Guess') ? 'FREE' : '+$9.00'}
                      </span>
                    </div>)}
                </div>

                <div className="border-t border-white/10 pt-3 mb-3" data-id="element-343">
                  <div className="flex items-center justify-between text-sm" data-id="element-344">
                    <span className="text-gray-400" data-id="element-345">Total Value</span>
                    <span className="text-gray-400 line-through" data-id="element-346">$144.00</span>
                  </div>
                </div>

                <div className="border-t border-[#D4A853]/40 pt-3 mb-5" data-id="element-347">
                  <div className="flex items-center justify-between" data-id="element-348">
                    <span className="text-white font-bold text-sm" data-id="element-349">
                      Today You Only Pay
                    </span>
                    <span className="text-[#D4A853] font-bold text-xl" data-id="element-350">
                      ${totalDollars}.{totalCents}
                    </span>
                  </div>
                </div>

                {/* Dynamic Buy Button */}
                <a href={getCheckoutUrl()} onClick={trackInitiateCheckout} className="block w-full bg-[#D4A853] hover:bg-[#c29642] text-white text-xl font-bold py-5 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 text-center" data-id="element-351">
                  Complete Purchase
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-[#1a3354]" data-id="element-352">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-353">
          <p className="text-gray-400 text-sm" data-id="element-354">
            © {new Date().getFullYear()} Couch Cash for Grandmas. All rights
            reserved.
          </p>
          <div className="flex items-center gap-6" data-id="element-355">
            <button onClick={() => setPrivacyOpen(true)} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-356">
              Privacy Policy
            </button>
            <button onClick={() => setTermsOpen(true)} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-357">
              Terms of Service
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={privacyOpen} onClose={() => setPrivacyOpen(false)} title="Privacy Policy" data-id="element-358">
        <div className="space-y-4" data-id="element-359">
          <p className="text-sm text-gray-500" data-id="element-360">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-361">
            Information We Collect
          </h3>
          <p data-id="element-362">
            When you purchase our digital products, we collect only the
            information necessary to process your order, including your email
            address and payment information. Payment processing is handled
            securely by third-party providers.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-363">
            How We Use Your Information
          </h3>
          <p data-id="element-364">
            We use your information solely to deliver your purchased digital
            products and to send important updates about your purchase. We do
            not sell, rent, or share your personal information with third
            parties for marketing purposes.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-365">
            Digital Product Delivery
          </h3>
          <p data-id="element-366">
            Our products are delivered digitally. Once your purchase is
            complete, you will receive access to download your files. No
            physical products are shipped.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-367">Cookies</h3>
          <p data-id="element-368">
            Our website may use cookies to improve your browsing experience. You
            can choose to disable cookies in your browser settings.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-369">Your Rights</h3>
          <p data-id="element-370">
            You have the right to access, correct, or delete your personal
            information. You may also opt out of marketing communications at any
            time.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-371">
            Changes to This Policy
          </h3>
          <p data-id="element-372">
            We may update this privacy policy from time to time. Any changes
            will be posted on this page with an updated revision date.
          </p>
        </div>
      </Modal>

      {/* Terms of Service Modal */}
      <Modal isOpen={termsOpen} onClose={() => setTermsOpen(false)} title="Terms of Service" data-id="element-373">
        <div className="space-y-4" data-id="element-374">
          <p className="text-sm text-gray-500" data-id="element-375">
            Last updated:{' '}
            {new Date().toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })}
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-376">
            Digital Product Purchase
          </h3>
          <p data-id="element-377">
            By purchasing "Couch Cash for Grandmas," you are buying a digital
            ebook guide. This is an educational product designed to help you
            learn how to create and sell printable recipe cards.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-378">
            What You're Getting
          </h3>
          <p data-id="element-379">
            This product is a step-by-step instructional guide. It does not
            include pre-made recipe cards, templates for resale, or any physical
            products. Results depend on your own effort and implementation.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-380">No Guarantees</h3>
          <p data-id="element-381">
            While we provide actionable guidance, we cannot guarantee any
            specific income or results. Success depends on many factors
            including your effort, market conditions, and execution.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-382">Refund Policy</h3>
          <p data-id="element-383">
            Due to the digital nature of this product, all sales are final. Once
            you receive access to download the ebook, refunds are not available.
            Please read the product description carefully before purchasing.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-384">
            Intellectual Property
          </h3>
          <p data-id="element-385">
            This ebook is for your personal use only. You may not resell,
            redistribute, or share the content with others. The content is
            protected by copyright.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-386">
            Limitation of Liability
          </h3>
          <p data-id="element-387">
            We are not liable for any damages or losses resulting from your use
            of this product or your business activities. This guide is for
            educational purposes only.
          </p>

          <h3 className="font-bold text-[#1E3A5F] text-lg" data-id="element-388">Agreement</h3>
          <p data-id="element-389">
            By purchasing this product, you agree to these terms of service.
          </p>
        </div>
      </Modal>
    </div>;
}